import classNames from 'classnames';
import { type TestId } from '@repo/test-id';
import { tr } from '@repo/locale';

import { useSplashScreen } from '@features/splashScreen/hooks/useSplashScreen';
import TextAnimation from '@features/splashScreen/components/TextAnimation';

import logoGif from '@assets/images/parkapp-logo.gif';

interface Props {
  testId: TestId;
}

export default function Overlay({ testId }: Props) {
  const { isVisible, fadeOut, animationState } = useSplashScreen();
  const { showLogo, showTextLine1, showTextLine2, showUnderline, showImages } = animationState;

  if (!isVisible) {
    return null;
  }

  return (
    <div
      data-testid={testId}
      className={classNames(
        'bg-splash-screen z-takeover fixed inset-0 flex flex-col items-center justify-between gap-4 overflow-hidden transition-opacity duration-1000 ease-in-out md:hidden',
        {
          'opacity-0': fadeOut,
          'opacity-100': !fadeOut,
        }
      )}
    >
      <div
        data-testid={testId.append('gradient')}
        aria-hidden
        className={classNames(
          'bg-splash-screen-gradient pointer-events-none absolute right-0 bottom-0 h-[437px] w-[437px] rounded-full blur-[134.7px] transition-opacity duration-1000',
          {
            'opacity-100': showImages,
            'opacity-0': !showImages,
          }
        )}
      />

      <div
        data-testid={testId.append('logo')}
        className={classNames('mt-[100px] flex w-[80%] items-center justify-center transition-all duration-700', {
          'translate-y-0 opacity-100': showLogo,
          'translate-y-4 opacity-0': !showLogo,
        })}
      >
        <img src={logoGif} alt={tr('splash_screen:logo_alt')} className="object-contain" />
      </div>

      <TextAnimation
        testId={testId}
        showTextLine1={showTextLine1}
        showTextLine2={showTextLine2}
        showUnderline={showUnderline}
      />

      <div className="relative flex w-full flex-1 items-end justify-center">
        <div
          data-testid={testId.append('parking')}
          aria-hidden
          className={classNames(
            'bg-image-parking absolute bottom-0 left-0 aspect-151/121 h-[min(302px,45vh)] w-auto bg-contain bg-bottom-left bg-no-repeat transition-all duration-1000',
            {
              'translate-x-0 opacity-100': showImages,
              '-translate-x-full opacity-0': !showImages,
            }
          )}
        />

        <div
          data-testid={testId.append('moose')}
          aria-hidden
          className={classNames(
            'bg-image-moose absolute right-0 bottom-0 aspect-236/281 h-[min(562px,70vh)] w-auto bg-contain bg-bottom-right bg-no-repeat transition-all duration-1000',
            {
              'translate-x-0 opacity-100': showImages,
              'translate-x-full opacity-0': !showImages,
            }
          )}
        />
      </div>
    </div>
  );
}
